import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  CartesianGrid,
  LabelList,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { IChartProps, StickyTooltip } from '../../../../../component/charts';
import ChartCard from '../../../../../component/charts/chartCard';
import {
  getMaxValueInArray,
  getMinValueInArray,
} from '../../../../../helpers/arrayHelper';
import { colors } from '../../../../../shared/constants';
import {
  labelFormatterMoneyAsInteger,
  labelFormatterNumber,
} from '../../../../../shared/formatters/formatters';
import { PricePerSqMtMedianChartData } from '../types/realtyTransactions.types';

export const PricePerSqMtMedian: React.FC = () => {
  const { requestStatusT, chartsDataTransactions } = useSelector(
    (state: any) => state.realtyTransactionsState
  );
  return (
    <ChartCard
      data={chartsDataTransactions['price_per_sq_mt_median']}
      cy={'PricePerSqMtMedian'}
      id={'pricePerSqMtMedian'}
      Chart={PricePerSqMtMedianChart}
      requestStatus={requestStatusT['migratoryOrigin']}
      hasAnnualDataOnly
    />
  );
};

const PricePerSqMtMedianChart: React.FC<
  IChartProps<PricePerSqMtMedianChartData[]>
> = ({ data, cy, id }) => {
  const labelFormatterPricePerSqMt = (value: number): string =>
    `${labelFormatterMoneyAsInteger(value)}/m²`;
  const dataTipInteger = (value: number): string => `${value.toFixed(0)}`;
  const [hasClicked, setHasClicked] = useState<boolean>(false);
  const [recordTooltip, setRecordTooltip] = useState<JSX.Element | null>(null);
  const [stopChange, setStopChange] = useState<boolean>(false);
  const formatterFunctions = [labelFormatterNumber, labelFormatterNumber];

  const spacing = 0.1 * getMaxValueInArray(data, 'value');

  const domain = [
    getMinValueInArray(data, 'value') - spacing,
    getMaxValueInArray(data, 'value') + spacing,
  ];
  const delta = domain[1] - domain[0];
  const ticks = [
    domain[0],
    domain[0] + delta / 4,
    domain[0] + (2 * delta) / 4,
    domain[0] + (3 * delta) / 4,
    domain[1],
  ];

  return (
    <ResponsiveContainer
      width="100%"
      height="100%"
      data-cy={`${cy}ChartContainer`}
    >
      <LineChart
        data={data}
        margin={{ top: 20, right: 50, left: 60, bottom: 5 }}
        onClick={() => setHasClicked(!hasClicked)}
        id={id}
      >
        <Tooltip
          wrapperStyle={{ visibility: 'visible' }}
          content={
            <StickyTooltip
              hasClicked={hasClicked}
              recordTooltip={recordTooltip}
              setRecordTooltip={setRecordTooltip}
              stopChange={stopChange}
              setStopChange={setStopChange}
              formatterFunctions={formatterFunctions}
            />
          }
        />
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="period" />
        <YAxis
          tickCount={4}
          ticks={ticks}
          tickFormatter={labelFormatterPricePerSqMt}
          domain={domain}
        />
        <Line
          dataKey="value"
          stroke={colors[2]}
          name="Preço/m²"
          strokeWidth={3}
        >
          <LabelList formatter={dataTipInteger} position="top" />
        </Line>
      </LineChart>
    </ResponsiveContainer>
  );
};
